import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../shared/cp-confirm-dialog.js';

interface BetterStackSource {
  id: number;
  display_name: string;
  source_token: string | null;
  signing_secret: string | null;
  target_project_id: number | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface Project {
  id: number;
  name: string;
  status: string;
}

@customElement('cp-betterstack-settings')
export class CpBetterStackSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _sources: BetterStackSource[] = [];
  @state() private _projects: Project[] = [];
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _successMessage = '';

  // Form state
  @state() private _showForm = false;
  @state() private _editingId: number | null = null;
  @state() private _formDisplayName = '';
  @state() private _formSourceToken = '';
  @state() private _formSigningSecret = '';
  @state() private _formProjectId: number | null = null;
  @state() private _saving = false;

  // Copy state
  @state() private _copySuccess = false;

  // Delete confirm state
  @state() private _deleteTarget: BetterStackSource | null = null;

  // Toggle state
  @state() private _togglingId: number | null = null;

  private readonly _webhookUrl = `${window.location.origin}/api/webhooks/betterstack`;

  connectedCallback(): void {
    super.connectedCallback();
    this._loadData();
  }

  private async _loadData(): Promise<void> {
    this._loading = true;
    try {
      const [sourcesRes, projectsRes] = await Promise.all([
        fetch('/api/betterstack/sources', { credentials: 'include' }),
        fetch('/api/projects', { credentials: 'include' }),
      ]);
      if (!sourcesRes.ok) throw new Error('Fehler beim Laden der Sources');
      if (!projectsRes.ok) throw new Error('Fehler beim Laden der Projekte');

      this._sources = await sourcesRes.json() as BetterStackSource[];
      this._projects = (await projectsRes.json() as Project[]).filter(p => p.status === 'active');
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._loading = false;
    }
  }

  private _openCreateForm(): void {
    this._editingId = null;
    this._formDisplayName = '';
    this._formSourceToken = '';
    this._formSigningSecret = '';
    this._formProjectId = null;
    this._showForm = true;
    this._error = '';
    this._successMessage = '';
  }

  private _openEditForm(source: BetterStackSource): void {
    this._editingId = source.id;
    this._formDisplayName = source.display_name;
    this._formSourceToken = '';
    this._formSigningSecret = '';
    this._formProjectId = source.target_project_id;
    this._showForm = true;
    this._error = '';
    this._successMessage = '';
  }

  private _closeForm(): void {
    this._showForm = false;
    this._editingId = null;
  }

  private async _saveSource(): Promise<void> {
    if (!this._formDisplayName.trim()) {
      this._error = 'Display Name ist erforderlich';
      return;
    }
    if (this._saving) return;
    this._saving = true;
    this._error = '';

    try {
      const body: Record<string, unknown> = {
        display_name: this._formDisplayName.trim(),
        target_project_id: this._formProjectId,
      };

      if (this._editingId === null) {
        // Create
        if (this._formSourceToken.trim()) body.source_token = this._formSourceToken.trim();
        if (this._formSigningSecret.trim()) body.signing_secret = this._formSigningSecret.trim();

        const res = await fetch('/api/betterstack/sources', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({ error: 'Fehler beim Erstellen' })) as { error: string };
          throw new Error(data.error || 'Fehler beim Erstellen');
        }
        this._successMessage = 'Source erstellt';
      } else {
        // Update
        if (this._formSourceToken.trim()) body.source_token = this._formSourceToken.trim();
        if (this._formSigningSecret.trim()) body.signing_secret = this._formSigningSecret.trim();

        const res = await fetch(`/api/betterstack/sources/${this._editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({ error: 'Fehler beim Aktualisieren' })) as { error: string };
          throw new Error(data.error || 'Fehler beim Aktualisieren');
        }
        this._successMessage = 'Source aktualisiert';
      }

      this._closeForm();
      await this._loadData();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._saving = false;
    }
  }

  private _requestDelete(source: BetterStackSource): void {
    this._deleteTarget = source;
  }

  private async _confirmDelete(): Promise<void> {
    if (!this._deleteTarget) return;
    const id = this._deleteTarget.id;
    this._deleteTarget = null;
    this._error = '';
    this._successMessage = '';
    try {
      const res = await fetch(`/api/betterstack/sources/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Fehler beim Loeschen' })) as { error: string };
        throw new Error(data.error || 'Fehler beim Loeschen');
      }
      this._successMessage = 'Source geloescht';
      await this._loadData();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    }
  }

  private _cancelDelete(): void {
    this._deleteTarget = null;
  }

  private async _toggleEnabled(source: BetterStackSource): Promise<void> {
    if (this._togglingId !== null) return;
    this._togglingId = source.id;
    this._error = '';
    try {
      const res = await fetch(`/api/betterstack/sources/${source.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !source.enabled }),
      });
      if (!res.ok) throw new Error('Fehler beim Umschalten');
      await this._loadData();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._togglingId = null;
    }
  }

  private async _copyWebhookUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this._webhookUrl);
      this._copySuccess = true;
      setTimeout(() => { this._copySuccess = false; }, 2000);
    } catch {
      this._error = 'Kopieren in Zwischenablage fehlgeschlagen';
    }
  }

  private _getProjectName(projectId: number | null): string {
    if (projectId === null) return '-';
    const project = this._projects.find(p => p.id === projectId);
    return project ? project.name : `ID ${projectId}`;
  }

  private _renderWebhookUrl() {
    return html`
      <div class="cp-bts__section">
        <h4 class="cp-bts__section-title">Webhook URL</h4>
        <div class="cp-bts__webhook-row">
          <code class="cp-bts__webhook-url">${this._webhookUrl}</code>
          <button
            class="cp-bts__btn ${this._copySuccess ? 'cp-bts__btn--success' : 'cp-bts__btn--secondary'}"
            @click=${this._copyWebhookUrl}
          >${this._copySuccess ? 'Kopiert!' : 'Kopieren'}</button>
        </div>
        <p class="cp-bts__hint">Diese URL in BetterStack als Webhook-Endpoint eintragen.</p>
      </div>
    `;
  }

  private _renderSourcesTable() {
    return html`
      <div class="cp-bts__section">
        <div class="cp-bts__section-header">
          <h4 class="cp-bts__section-title">Sources</h4>
          <button
            class="cp-bts__btn cp-bts__btn--primary"
            @click=${this._openCreateForm}
          >Source hinzufuegen</button>
        </div>

        ${this._loading ? html`
          <p class="cp-bts__loading">Sources werden geladen...</p>
        ` : html`
          ${this._sources.length > 0 ? html`
            <table class="cp-bts__table">
              <thead>
                <tr>
                  <th class="cp-bts__th">Name</th>
                  <th class="cp-bts__th">Projekt</th>
                  <th class="cp-bts__th">Status</th>
                  <th class="cp-bts__th"></th>
                </tr>
              </thead>
              <tbody>
                ${this._sources.map(source => html`
                  <tr class="cp-bts__tr">
                    <td class="cp-bts__td">${source.display_name}</td>
                    <td class="cp-bts__td">${this._getProjectName(source.target_project_id)}</td>
                    <td class="cp-bts__td">
                      <button
                        class="cp-bts__toggle ${source.enabled ? 'cp-bts__toggle--on' : 'cp-bts__toggle--off'}"
                        @click=${() => this._toggleEnabled(source)}
                        ?disabled=${this._togglingId === source.id}
                        aria-label="${source.enabled ? 'Deaktivieren' : 'Aktivieren'}"
                        aria-pressed="${source.enabled}"
                      >
                        <span class="cp-bts__toggle-knob"></span>
                      </button>
                    </td>
                    <td class="cp-bts__td cp-bts__td--actions">
                      <button
                        class="cp-bts__btn cp-bts__btn--ghost"
                        @click=${() => this._openEditForm(source)}
                      >Bearbeiten</button>
                      <button
                        class="cp-bts__btn cp-bts__btn--danger"
                        @click=${() => this._requestDelete(source)}
                      >Loeschen</button>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          ` : html`
            <p class="cp-bts__empty">Keine Sources konfiguriert.</p>
          `}
        `}
      </div>
    `;
  }

  private _renderForm() {
    if (!this._showForm) return nothing;

    const isEdit = this._editingId !== null;
    const title = isEdit ? 'Source bearbeiten' : 'Neue Source hinzufuegen';

    return html`
      <div class="cp-bts__section">
        <h4 class="cp-bts__section-title">${title}</h4>
        <div class="cp-bts__form">
          <div class="cp-bts__form-field">
            <label class="cp-bts__label">Display Name *</label>
            <input
              class="cp-bts__input"
              type="text"
              placeholder="z.B. PELORUS Logs"
              .value=${this._formDisplayName}
              @input=${(e: Event) => { this._formDisplayName = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <div class="cp-bts__form-field">
            <label class="cp-bts__label">Source Token</label>
            <input
              class="cp-bts__input"
              type="password"
              placeholder="${isEdit ? 'Leer lassen um beizubehalten' : 'BetterStack Source Token'}"
              .value=${this._formSourceToken}
              @input=${(e: Event) => { this._formSourceToken = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <div class="cp-bts__form-field">
            <label class="cp-bts__label">Signing Secret</label>
            <input
              class="cp-bts__input"
              type="password"
              placeholder="${isEdit ? 'Leer lassen um beizubehalten' : 'HMAC Signing Secret'}"
              .value=${this._formSigningSecret}
              @input=${(e: Event) => { this._formSigningSecret = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <div class="cp-bts__form-field">
            <label class="cp-bts__label">Projekt</label>
            <select
              class="cp-bts__select"
              .value=${String(this._formProjectId ?? '')}
              @change=${(e: Event) => {
                const val = (e.target as HTMLSelectElement).value;
                this._formProjectId = val ? Number(val) : null;
              }}
            >
              <option value="">Kein Projekt</option>
              ${this._projects.map(p => html`
                <option value="${p.id}" ?selected=${this._formProjectId === p.id}>${p.name}</option>
              `)}
            </select>
          </div>
          <div class="cp-bts__form-actions">
            <button
              class="cp-bts__btn cp-bts__btn--primary"
              @click=${this._saveSource}
              ?disabled=${!this._formDisplayName.trim() || this._saving}
            >${this._saving ? 'Speichert...' : 'Speichern'}</button>
            <button
              class="cp-bts__btn cp-bts__btn--secondary"
              @click=${this._closeForm}
            >Abbrechen</button>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-bts">
        <div class="cp-bts__header">
          <h3 class="cp-bts__title">BetterStack Logging</h3>
        </div>

        ${this._error ? html`
          <div class="cp-bts__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-bts__success">${this._successMessage}</div>
        ` : nothing}

        ${this._renderWebhookUrl()}
        ${this._renderSourcesTable()}
        ${this._renderForm()}

        <cp-confirm-dialog
          .open=${this._deleteTarget !== null}
          title="Source loeschen"
          message="Source '${this._deleteTarget?.display_name ?? ''}' wirklich loeschen?"
          confirmLabel="Loeschen"
          variant="danger"
          @dialog-confirmed=${this._confirmDelete}
          @dialog-cancelled=${this._cancelDelete}
        ></cp-confirm-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-betterstack-settings': CpBetterStackSettings;
  }
}
